import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_BOOST_PACKAGES, GET_MY_BOOST_REQUESTS } from '../graphql/queries';
import { REQUEST_BOOST } from '../graphql/mutations';
import Layout from '../components/Layout';
import { Card, Button, Alert, Row, Col } from 'react-bootstrap';
import { Lightning, CheckCircle, Info } from 'react-bootstrap-icons';

const Boost: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const adSlug = searchParams.get('ad');

  const [selectedPackage, setSelectedPackage] = useState<any>(null);
  const [selectedDays, setSelectedDays] = useState<number>(7);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { data: packagesData, loading: packagesLoading } = useQuery(GET_BOOST_PACKAGES);
  const { data: requestsData } = useQuery(GET_MY_BOOST_REQUESTS, {
    variables: { status: 'pending' }
  });

  const [requestBoost, { loading: boosting }] = useMutation(REQUEST_BOOST, {
    onCompleted: (data) => {
      if (data.requestBoost.success) {
        const boostRequest = data.requestBoost.boostRequest;
        // Navigate to payment page
        navigate(`/boost/payment/${boostRequest.id}`);
      } else {
        setError(data.requestBoost.message);
      }
    },
    onError: (err) => {
      setError(err.message);
    }
  });

  const packages = packagesData?.boostPackages || [];
  const pendingRequests = requestsData?.myBoostRequests || [];

  const handlePackageSelect = (pkg: any) => {
    setSelectedPackage(pkg);
    setSelectedDays(pkg.durationDays);
    setError('');
  };

  const handleDaysChange = (days: number) => {
    setSelectedDays(days);
    setSelectedPackage(null);
    setError('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!adSlug) {
      setError('Ad not specified. Please select an ad to boost.');
      return;
    }

    if (!selectedPackage && !selectedDays) {
      setError('Please select a package or duration.');
      return;
    }

    // Find ad by slug - we'll need to query for the ad ID
    // For now, we'll pass the slug and let the backend handle it
    // This is a simplified approach - ideally we should query the ad first

    // Calculate price for custom duration
    const packageId = selectedPackage?.id || null;

    requestBoost({
      variables: {
        adId: adSlug, // This should be the ad ID, not slug - we'll fix this
        packageId,
        durationDays: selectedDays
      }
    });
  };

  const calculatePrice = (days: number) => {
    return days * 100; // Rs. 100 per day
  };

  return (
    <Layout showSidebar={false}>
      <div className="container py-4">
        <div className="mb-4">
          <h1 className="display-6 fw-bold mb-2">Boost Your Ad</h1>
          <p className="text-muted">
            Get more visibility by featuring your ad on NayaBajar
          </p>
        </div>

        {error && (
          <Alert variant="danger" className="mb-4" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" className="mb-4" dismissible onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Pending Requests Warning */}
        {pendingRequests.length > 0 && (
          <Alert variant="warning" className="mb-4">
            <div className="d-flex align-items-start gap-3">
              <Info className="flex-shrink-0 mt-1" />
              <div>
                <h5 className="fw-bold">Pending Boost Requests</h5>
                <p className="small mb-2">
                  You have {pendingRequests.length} pending boost request(s). Please complete payment for existing requests before creating new ones.
                </p>
                <Button
                  variant="link"
                  className="p-0 fw-bold text-warning-emphasis"
                  onClick={() => navigate('/profile')}
                >
                  View My Boost Requests →
                </Button>
              </div>
            </div>
          </Alert>
        )}

        <Card className="mb-4 shadow-sm">
          <Card.Body>
            <h2 className="h5 fw-semibold mb-4">Select Boost Package</h2>

            {packagesLoading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-muted">Loading packages...</p>
              </div>
            ) : packages.length > 0 ? (
              <Row className="g-3 mb-4">
                {packages.map((pkg: any) => (
                  <Col md={4} key={pkg.id}>
                    <Card
                      onClick={() => handlePackageSelect(pkg)}
                      className={`h-100 cursor-pointer transition-all ${
                        selectedPackage?.id === pkg.id
                          ? 'border-primary bg-primary bg-opacity-10'
                          : 'border-secondary-subtle hover-border-primary'
                      }`}
                      style={{ cursor: 'pointer' }}
                    >
                      <Card.Body className="position-relative">
                        {selectedPackage?.id === pkg.id && (
                          <div className="position-absolute top-0 end-0 m-3">
                            <CheckCircle className="text-primary" />
                          </div>
                        )}
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <Lightning className="text-primary" />
                          <h3 className="h6 fw-semibold mb-0">{pkg.name}</h3>
                        </div>
                        <p className="display-6 fw-bold text-primary mb-2">
                          Rs. {pkg.price}
                        </p>
                        <p className="small text-muted mb-2">{pkg.durationDays} days</p>
                        {pkg.description && (
                          <p className="small text-secondary">{pkg.description}</p>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            ) : (
              <p className="text-muted mb-3">No packages available. Select custom duration below.</p>
            )}

            <div className="border-top pt-4">
              <h3 className="h6 fw-semibold mb-3">Or Select Custom Duration</h3>
              <Row className="g-2">
                {[3, 7, 14, 30].map((days) => (
                  <Col xs={6} md={3} key={days}>
                    <Button
                      variant={`outline-${selectedDays === days && !selectedPackage ? 'primary' : 'secondary'}`}
                      className={`w-100 h-100 d-flex flex-column align-items-center justify-content-center py-3 ${
                        selectedDays === days && !selectedPackage ? 'bg-primary bg-opacity-10 border-primary' : ''
                      }`}
                      onClick={() => handleDaysChange(days)}
                    >
                      <div className="text-center">
                        <div className="display-6 fw-bold">{days}</div>
                        <div className="small text-muted">Days</div>
                        <div className="h6 fw-semibold text-primary mt-2">
                          Rs. {calculatePrice(days)}
                        </div>
                      </div>
                    </Button>
                  </Col>
                ))}
              </Row>
            </div>

            {/* Summary */}
            <div className="mt-4 p-3 bg-light rounded">
              <h3 className="h6 fw-semibold mb-2">Boost Summary</h3>
              <div className="d-flex flex-column gap-2 small">
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Duration:</span>
                  <span className="fw-medium">
                    {selectedDays} days
                    {selectedPackage && ` (${selectedPackage.name})`}
                  </span>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Amount to Pay:</span>
                  <span className="fw-bold text-primary">
                    Rs. {selectedPackage ? selectedPackage.price : calculatePrice(selectedDays)}
                  </span>
                </div>
              </div>
            </div>

            <Button
              variant={boosting || pendingRequests.length > 0 ? 'secondary' : 'primary'}
              className="w-100 mt-3 py-3 fw-semibold"
              onClick={handleSubmit}
              disabled={boosting || pendingRequests.length > 0}
            >
              {boosting ? 'Processing...' : 'Continue to Payment'}
            </Button>

            {pendingRequests.length > 0 && (
              <p className="text-small text-center text-muted mt-2 mb-0">
                Complete pending payments before creating new boost requests
              </p>
            )}
          </Card.Body>
        </Card>

        {/* How it works */}
        <Card className="shadow-sm">
          <Card.Body>
            <h2 className="h5 fw-semibold mb-4">How It Works</h2>
            <Row className="g-4">
              <Col xs={12}>
                <div className="d-flex align-items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center text-primary fw-bold" style={{ width: '32px', height: '32px' }}>
                      1
                    </div>
                  </div>
                  <div>
                    <h5 className="fw-medium mb-1">Select Package or Duration</h5>
                    <p className="small text-muted mb-0">Choose a pre-defined package or custom duration for your boost.</p>
                  </div>
                </div>
              </Col>
              <Col xs={12}>
                <div className="d-flex align-items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center text-primary fw-bold" style={{ width: '32px', height: '32px' }}>
                      2
                    </div>
                  </div>
                  <div>
                    <h5 className="fw-medium mb-1">Make Payment</h5>
                    <p className="small text-muted mb-0">Scan the QR code and pay the specified amount to our bank account.</p>
                  </div>
                </div>
              </Col>
              <Col xs={12}>
                <div className="d-flex align-items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center text-primary fw-bold" style={{ width: '32px', height: '32px' }}>
                      3
                    </div>
                  </div>
                  <div>
                    <h5 className="fw-medium mb-1">Upload Payment Proof</h5>
                    <p className="small text-muted mb-0">Upload a screenshot or bank statement as proof of payment.</p>
                  </div>
                </div>
              </Col>
              <Col xs={12}>
                <div className="d-flex align-items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center text-primary fw-bold" style={{ width: '32px', height: '32px' }}>
                      4
                    </div>
                  </div>
                  <div>
                    <h5 className="fw-medium mb-1">Get Featured</h5>
                    <p className="small text-muted mb-0">Once approved, your ad will be featured for the selected duration.</p>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </div>
    </Layout>
  );
};

export default Boost;
